// ⭐ FINAL VIEW NOTICE PAGE — MATCHING SummaryDetails STYLING EXACTLY
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminNavbar from "../AdminNav";
import {
  Box,
  Typography,
  Button,
  ButtonBase,
  CircularProgress,
  Paper,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import api from "../../api/api";
import { ENDPOINTS } from "../../api/endpoints";
import { useLocationLabels } from "../../hooks/useLocationLabels";
import { addSpaceAfterCommas } from "../../utils/textUtils";

const Pipe = () => <span style={{ color: "#eee", margin: "0 2px" }}>|</span>;
const Label = ({ children }) => (
  <span style={{ color: "#000", fontWeight: 500 }}>{children}</span>
);

function FileLink({ url, label }) {
  if (!url) return null;
  const lower = url.toLowerCase();
  const isImage = /\.(jpe?g|png|gif|bmp|webp)$/i.test(lower);
  const Icon = isImage ? ImageIcon : DescriptionIcon;

  return (
    <ButtonBase
      onClick={() => window.open(url, "_blank", "noopener")}
      sx={{ display: "flex", alignItems: "center", mt: 2 }}
    >
      <Icon fontSize="large" sx={{ mr: 1, color: "common.black" }} />
      <Typography
        variant="body2"
        noWrap
        sx={{ color: "common.black", textDecoration: "none" }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}

function formatHeight(inches) {
  if (!inches || inches <= 0) return "Any";
  const ft = Math.floor(inches / 12);
  const rem = inches % 12;
  return `${ft}′${rem}″`;
}

export default function ViewNotice() {
  const { noticeId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  const { loading: locLoading, formatLocations } = useLocationLabels();

  useEffect(() => {
    (async () => {
      try {
        const [projRes, rolesRes] = await Promise.all([
          api.get(
            ENDPOINTS.getNoticeDetails({
              noticeId,
              email: "",
              page: 1,
              pageSize: 50,
            })
          ),
          api.get(
            ENDPOINTS.getRoleDetails({
              noticeId,
              page: 1,
              pageSize: 50,
            })
          ),
        ]);

        const projects = projRes?.data?.data || [];
        setProject(
          projects.find((p) => Number(p.noticeId) === Number(noticeId)) || null
        );
        setRoles(rolesRes?.data?.data?.items || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [noticeId]);

  if (loading || locLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!project) return null;

  return (
    <div className="backgroundColor">
      <AdminNavbar />

      <Box
        sx={{
          maxWidth: 1200,
          m: "auto",
          mt: 4,
          mb: 6,
          boxShadow: 3,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Paper
          square
          elevation={0}
          sx={{
            bgcolor: "grey.900",
            color: "white",
            borderRadius: "9px",
          }}
        >
          <Box
            sx={{
              position: "relative",
              backgroundColor: "#202020",
              color: "white",
              borderRadius: "8px",
              padding: "16px 20px",
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            {project.rushCall && (
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "32px",
                  backgroundColor: "#d17c7c",
                  color: "#fff",
                  writingMode: "sideways-lr",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                Rush Call
              </Box>
            )}

            <Box sx={{ marginLeft: project.rushCall ? "48px" : 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 400 }}>
                {project.projectTitle}
              </Typography>

              {!!project.auditionLocations && (
                <Typography variant="body2" sx={{ mt: 1, fontSize: "13px" }}>
                  <span className="lightGrey">Seeking Talent From: </span>
                  <span style={{ color: "#90b5fe" }}>
                    {formatLocations(project.auditionLocations)}
                  </span>
                </Typography>
              )}

             <Typography variant="body2" sx={{ mt: 1 }}>
  <span className="lightGrey">Union Status:</span>{" "}
  {project.unionStatus
    ? project.unionStatus.split(",").join(", ")
    : "N/A"}
</Typography>

              <Typography variant="body2">
                <span className="lightGrey">Pay:</span>{" "}
                {project.payScale || "N/A"}
              </Typography>

              <Typography variant="body2">
                <span className="lightGrey">Posted:</span>{" "}
                <span style={{ color: "#90b5fe" }}>
                  {new Date(project.noticeCreatedDate).toLocaleDateString()}
                </span>

                <span style={{ color: "#ff9f9f", paddingLeft: "10px" }}>
                  Deadline:
                </span>{" "}
                <span style={{ color: "#ff9f9f" }}>
                  {new Date(project.noticeEndDate).toLocaleDateString()}
                </span>
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ bgcolor: "background.paper", p: 3 }}>
          {project.projectDescription
            ?.split(/\r\n\r\n/)
            .map((para, idx) => (
              <Typography
                key={idx}
                paragraph
                component="div"
                sx={{ fontSize: "1rem" }}
                dangerouslySetInnerHTML={{ __html: para }}
              />
            ))}

          <Box sx={{ display: "flex", gap: 2 }}>
            <FileLink url={project.scriptsPath} label="Script Reference" />
            <FileLink
              url={project.picturesandDocumentsPath}
              label="Photo Reference"
            />
            <FileLink
              url={project.photoReferencesPath}
              label="Photo References"
            />
          </Box>

          <Box sx={{ mt: 4 }}>
            {roles.map((role) => (
              <Paper key={role.roleId} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography sx={{ fontSize: "1rem", fontWeight: 500 }}>
                  {role.roleName}
                  {role.sex && (
                    <>
                      <Pipe />
                      <Label>Sex:</Label> {addSpaceAfterCommas(role.sex)}
                    </>
                  )}
                </Typography>

                {role.moreAboutRole && (
                  <Typography
                    sx={{ mt: 2, fontSize: "1rem" }}
                    dangerouslySetInnerHTML={{
                      __html: role.moreAboutRole.replace(/\r\n/g, "<br/>"),
                    }}
                  />
                )}
              </Paper>
            ))}
          </Box>

          {/* --- EDIT + CLOSE BUTTONS --- */}
          <Box sx={{ textAlign: "center", mt: 4, display: "flex", justifyContent: "center", gap: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                sessionStorage.setItem("editNoticeId", project.noticeId);
                sessionStorage.setItem("editNoticeData", JSON.stringify(project));
                navigate(`/post-casting-call/${project.noticeId}`, {
                  state: { casting: project, noticeId: project.noticeId },
                });
              }}
            >
              Edit Notice
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={() => window.close()}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Box>
    </div>
  );
}
